import {
  IActivity,
  IActivityUpdateRequest,
  IClientCreateRequest,
  IClientUpdateRequest,
  IMember,
  IMemberUpdateRequest,
  IUserUpdateRequest
} from "@wingspanhq/users/dist/lib/index";
import {
  IAccountUpdateRequest,
  INewUser,
  INewUserCreateResponse,
  ISession,
  IUserCreateRequest
} from "@wingspanhq/users/dist/lib/interfaces";
import { IPasswordResetRequest } from "@wingspanhq/users/dist/lib/interfaces/api/password";
import { WSMutationsConfig, WSQueryCache, WSQueryKey } from "@ws-react-query";
import { useCaptcha } from "../../components/Captcha";
import {
  ISessionCreateRequest,
  ISignInWithGoogleRequest,
  usersService
} from "../../services/users";
import { Await } from "../../utils";
import { analyticsService } from "@services/analyticsService";
import { WSServiceError } from "../../utils/serviceHelper";
import { useWSMutation } from "../helpers";
import { useUserId } from "../hooks/helpers";
import { QUERY_USERS_SESSION } from "../session";
import {
  QUERY_USERS_ACCOUNTS,
  QUERY_USERS_ACTIVITIES,
  QUERY_USERS_CLIENT,
  QUERY_USERS_MEMBER_PROFILE,
  QUERY_USERS_USER_PROFILE
} from "./keys";

export const useMutationUpdateActivity = (
  userId: string,
  config?: WSMutationsConfig<IActivity, WSServiceError>
) =>
  useWSMutation(
    async (request: IActivityUpdateRequest) => {
      const activity = await usersService.activity.update(userId, request);

      WSQueryCache.setQueryData([QUERY_USERS_ACTIVITIES, userId], activity);

      return activity;
    },
    {
      dependencies: [QUERY_USERS_ACTIVITIES],
      ...config
    }
  );

export const useMutationUpdateUserProfile = (
  config?: WSMutationsConfig<INewUser, WSServiceError> & {
    dependencies?: WSQueryKey[];
  }
) => {
  const userId = useUserId();

  return useWSMutation(
    async (request: IUserUpdateRequest) =>
      usersService.user.update(userId, request),
    {
      dependencies: [QUERY_USERS_USER_PROFILE],
      ...config
    }
  );
};

type CreateSessionRequest = {
  remember?: boolean;
} & ISessionCreateRequest;

export const useMutationCreateSession = (
  config?: WSMutationsConfig<ISession, WSServiceError, CreateSessionRequest>
) => {
  const callWithCaptcha = useCaptcha("createSession");

  return useWSMutation<ISession, WSServiceError, CreateSessionRequest>(
    ({ remember, ...request } = {}) =>
      callWithCaptcha((captchaToken, captchaVersion, forceCaptcha) =>
        usersService.session.create(
          { captchaToken, captchaVersion, forceCaptcha, ...request },
          { remember }
        )
      ),
    {
      dependencies: [QUERY_USERS_SESSION],
      ...config
    }
  );
};

export const useMutationIdentifyAuthentication = (
  config?: WSMutationsConfig<
    ReturnType<typeof usersService.authentication.identify>,
    WSServiceError,
    { email: string }
  >
) => {
  const callWithCaptcha = useCaptcha("identifyAuth");

  return useWSMutation<
    ReturnType<typeof usersService.authentication.identify>,
    WSServiceError,
    { email: string }
  >(payload =>
    callWithCaptcha((captchaToken, captchaVersion, forceCaptcha) =>
      usersService.authentication.identify({
        captchaToken,
        captchaVersion,
        ...payload
      })
    )
  );
};

export const useMutationSingleSignIn = (
  config?: WSMutationsConfig<
    Await<ReturnType<typeof usersService.authentication.singleSignIn>>,
    WSServiceError,
    { code: string }
  >
) => {
  return useWSMutation<
    Await<ReturnType<typeof usersService.authentication.singleSignIn>>,
    WSServiceError,
    { code: string }
  >(payload => usersService.authentication.singleSignIn(payload), {
    dependencies: [QUERY_USERS_SESSION],
    ...config
  });
};

// 2FA

export const useMutationCreateSessionWithGoogleAuthToken = () =>
  useWSMutation(
    (request: ISignInWithGoogleRequest) =>
      usersService.session.socialAuth.google(request),
    {
      dependencies: [QUERY_USERS_SESSION]
    }
  );

export const useMutationUpdateMemberProfile = (
  config?: WSMutationsConfig<IMember, WSServiceError>
) =>
  useWSMutation(
    async (request: IMemberUpdateRequest) =>
      usersService.member.update(request.memberId, request),
    {
      dependencies: [QUERY_USERS_MEMBER_PROFILE],
      ...config
    }
  );

export const useMutationDeleteAccount = () =>
  useWSMutation(
    async (props: { accountId: string; deleteTransactions?: boolean }) =>
      usersService.account.delete(props.accountId, props.deleteTransactions),
    {
      dependencies: [QUERY_USERS_ACCOUNTS]
    }
  );

export const useMutationUpdateAccount = () =>
  useWSMutation(
    async (props: IAccountUpdateRequest & { accountId: string }) => {
      const { accountId, ...data } = props;

      return await usersService.account.update(accountId, data);
    },
    {
      dependencies: [QUERY_USERS_ACCOUNTS]
    }
  );

export const useMutationCreateAccount = () =>
  useWSMutation(usersService.account.create, {
    dependencies: [QUERY_USERS_ACCOUNTS]
  });

type CreateNewUserRequest = IUserCreateRequest & {
  inviteCode?: string;
  context?: string;
};

export const useMutationCreateUser = () => {
  const callWithCaptcha = useCaptcha("createUser");
  return useWSMutation<
    INewUserCreateResponse,
    WSServiceError,
    CreateNewUserRequest
  >(
    (data: CreateNewUserRequest) => {
      const isPublicSignUp = data.context === "PublicSignUp";
      return callWithCaptcha((captchaToken, captchaVersion, forceCaptcha) =>
        usersService.user.create(
          {
            captchaToken,
            captchaVersion,
            forceCaptcha,
            ...data
          },
          isPublicSignUp
        )
      );
    },
    {
      dependencies: [QUERY_USERS_USER_PROFILE]
    }
  );
};

export const useMutationCreateClient = (userId: string) =>
  useWSMutation(
    (data: IClientCreateRequest) => usersService.client.create(userId, data),
    {
      dependencies: [QUERY_USERS_CLIENT]
    }
  );

export const useMutationCreateClientWithoutUserId = () =>
  useWSMutation(
    (params: { userId: string; data: IClientCreateRequest }) =>
      usersService.client.create(params.userId, params.data),
    {
      dependencies: [QUERY_USERS_CLIENT]
    }
  );

export const useMutationUpdateClient = (userId: string) =>
  useWSMutation(
    (data: IClientUpdateRequest) => usersService.client.update(userId, data),
    {
      dependencies: [QUERY_USERS_CLIENT]
    }
  );

// When the email isn't registered, the API rejects the reset request with
// "User <email> doesn't exist". We must not surface this — see PLA-2915.
const USER_NOT_FOUND_ERROR_REGEXP = /user .*does(n['’]?t| not) exist/i;

const isUserNotFoundError = (error: unknown): boolean => {
  const message = (error as WSServiceError)?.response?.data?.error;
  return (
    typeof message === "string" && USER_NOT_FOUND_ERROR_REGEXP.test(message)
  );
};

export const useMutationRequestPasswordReset = () => {
  const callWithCaptcha = useCaptcha("resetPassword");

  return useWSMutation(
    async (request: IPasswordResetRequest) => {
      try {
        return await callWithCaptcha(
          (captchaToken, captchaVersion, forceCaptcha) =>
            usersService.user.passwordReset.create({
              captchaToken,
              captchaVersion,
              forceCaptcha,
              ...request
            } as any)
        );
      } catch (error) {
        // Security: never reveal whether an account exists for a given email
        // (prevents account enumeration). Swallow the "user doesn't exist" error
        // and resolve like a success so the confirmation is identical whether or
        // not the account exists. Any other error (captcha, network, server) is
        // account-independent, so it is rethrown to be shown to the user.
        if (isUserNotFoundError(error)) {
          return undefined;
        }
        throw error;
      }
    },

    {
      onSettled: () => {
        analyticsService.track("Request reset password");
      }
    }
  );
};

export const useMutationGetGuestAccountNumbers = () =>
  useWSMutation((request: { accountId: string; publicToken: string }) =>
    usersService.guest.accountNumbers(request)
  );

export const useMutationRequestOrganizationUser = () => {
  return useWSMutation(
    (userId: string) => usersService.organization.users.session(userId),
    {
      dependencies: [QUERY_USERS_SESSION]
    }
  );
};
